import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, getMe } from '../lib/api';
import ProposalCard from '../components/ProposalCard';
import Invites from '../components/Invites';
import SafetyMenu from '../components/SafetyMenu';
import MentionsList from '../components/MentionsList';
import Avatar from '../components/Avatar';
import EmojiReactions from '../components/EmojiReactions';
import { MyReferrals } from '../components/Referral';
import { openProfile } from '../lib/nav';
import RichText from '../components/RichText';
import ChatLinkPreview from '../components/ChatLinkPreview';
import { AttachButton, ChatAttachment, PendingFile, uploadChatFile, useFileDrop } from '../components/ChatFile';

/* Connections and Messages, merged.

   They were two nav items that will both be empty for a long time at this
   size, each with a note explaining why — which is how a quiet network starts
   feeling like an empty one. They are also the same object: a connection has
   exactly one thread, so listing them apart was always artificial.

   Nothing here explains what you are not allowed to do. The rule is worth
   stating once, positively, as something the product does for you. */

export default function People({ notify, threadRequest, onOpenPost }) {
  const me = getMe();
  const [proposals, setProposals] = useState([]);
  // Referrals waiting for you to confirm are waiting on you too.
  const [referralAsks, setReferralAsks] = useState(0);
  const [connections, setConnections] = useState([]);
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // Tabs: my connections / discover members / requests (in and out).
  const [tab, setTab] = useState('connections');
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [sent, setSent] = useState([]);
  const [menuFor, setMenuFor] = useState(null);
  const [connectTo, setConnectTo] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  // First visit: if something's waiting on you, land on Requests.
  const landed = useRef(false);
  const refreshDiscover = useCallback(() => {
    api.discover(q.trim()).then(setResults).catch(() => setResults([]));
  }, [q]);
  useEffect(() => {
    if (tab !== 'discover') return undefined;
    const t = setTimeout(refreshDiscover, 250);
    return () => clearTimeout(t);
  }, [tab, refreshDiscover]);

  const load = useCallback(async () => {
    const [p, c, t, refs] = await Promise.all([
      api.proposals().catch(() => []),
      api.connections().catch(() => []),
      api.threads().catch(() => []),
      api.myReferrals().catch(() => []),
    ]);
    const asks = refs.filter((r) => r.role === 'candidate' && r.status === 'pending_consent').length;
    setProposals(p);
    setReferralAsks(asks);
    if (!landed.current) { landed.current = true; if (p.length || asks) setTab('requests'); }
    api.sentRequests().then(setSent).catch(() => setSent([]));
    setConnections(c);
    setThreads(t);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function look() {
    setBusy(true);
    try {
      const r = await api.generateProposals();
      notify(
        r.proposals_created
          ? `${r.proposals_created} introduction${r.proposals_created > 1 ? 's' : ''} to look at`
          : 'Nothing new to suggest yet — comment on a post or react to the wire, and it will have more to go on'
      );
      load();
    } catch (e) {
      notify(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function sever(c) {
    if (!window.confirm(
      `End your connection with ${c.other.name}?\n\nThere is no announcement. Their posts stop reaching your feed and neither of you can send anything new, but everything already said stays readable to you both.`
    )) return;
    try {
      await api.sever(c.id);
      notify('Ended, quietly.');
      closeThread();
      load();
    } catch (e) {
      notify(e.message);
    }
  }

  // Copilot asked for a particular conversation.
  useEffect(() => {
    if (threadRequest?.id) openThread(threadRequest.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadRequest]);

  // Reply quotes one earlier message; edit fixes one of yours.
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [posting, setPosting] = useState(false);
  // A file waiting to go with the next message (already in your files).
  const [pendingFile, setPendingFile] = useState(null);
  const pick = useCallback(async (file) => {
    if (!file) return;
    setPosting(true);
    const f = await uploadChatFile(file, notify);
    setPosting(false);
    if (f) setPendingFile(f);
  }, [notify]);
  const { over, dropProps, onPaste } = useFileDrop(pick);
  useEffect(() => { setPendingFile(null); }, [open?.id]);

  // A reply, draft or edit belongs to one conversation: never carry it into the next.
  function closeThread() {
    setOpen(null);
    setReplyTo(null);
    setDraft('');
    setEditingId(null);
  }

  async function openThread(id) {
    try {
      const t = await api.thread(id);
      closeThread();
      setOpen(t);
    } catch (e) {
      notify(e.message);
    }
  }
  const swap = (u) => setOpen((o) => (o ? { ...o, messages: o.messages.map((x) => (x.id === u.id ? u : x)) } : o));

  async function send() {
    if ((!draft.trim() && !pendingFile) || !open?.active || posting) return;
    setPosting(true);
    try {
      await api.sendMessage(open.id, draft.trim(), replyTo?.id, pendingFile?.id);
      setDraft('');
      setReplyTo(null);
      setPendingFile(null);
      setOpen(await api.thread(open.id));
      load();
    } catch (e) {
      notify(e.message);
    } finally {
      setPosting(false);
    }
  }

  const active = connections.filter((c) => c.active);
  const ended = connections.filter((c) => !c.active);
  // A connection carries its own thread id, so no matching is needed.
  const lastMessage = (threadId) => threads.find((t) => t.id === threadId)?.last_message;
  const unreadIn = (threadId) => threads.find((t) => t.id === threadId)?.unread || 0;

  if (open) {
    return (
      <div className="people">
        <button className="linklike back" onClick={() => { closeThread(); load(); }}>
          ← Back to people
        </button>
        <div className={`msg-pane standalone${over ? ' drop-over' : ''}`} {...(open.active ? dropProps : {})}>
          {over && open.active && <div className="drop-hint">Drop to attach to your message</div>}
          <header className="msg-head">
            <button type="button" className="person-link name" onClick={() => openProfile(open.other.handle)}><b>{open.other.name}</b></button>
            <span className="muted-sm">{open.other.headline}</span>
            <SafetyMenu person={open.other} context="thread" contextId={open.id} notify={notify}
              onDone={() => { closeThread(); load(); }} />
          </header>

          {/* Newest first: write at the top, read down into the past. */}
          {open.active ? (
            <div className="msg-compose">
              {replyTo && (
                <div className="msg-replying">
                  <span>Replying to {replyTo.sender_id === me?.id ? 'yourself' : open.other.name.split(' ')[0]}: {(replyTo.body || '').slice(0, 80)}</span>
                  <button className="linklike" aria-label="Cancel reply" onClick={() => setReplyTo(null)}>×</button>
                </div>
              )}
              <PendingFile file={pendingFile} onRemove={() => setPendingFile(null)} />
              <input
                className="in" value={draft} placeholder="Write a message…"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                onPaste={onPaste}
              />
              <AttachButton disabled={posting} onPick={pick} label="" />
              <button className="btn primary" onClick={send} disabled={(!draft.trim() && !pendingFile) || posting}>Send</button>
            </div>
          ) : (
            <div className="msg-closed">
              This connection has ended. The conversation stays readable, but neither of you can
              add to it.
            </div>
          )}
          <div className="msg-body">
            {open.messages.length === 0 && (
              <p className="muted-sm">
                Nothing said yet. You are both here because you each said yes.
              </p>
            )}
            {[...open.messages].reverse().map((m) => (
              <div key={m.id} className={`bubble${m.sender_id === me?.id ? ' mine' : ''}${m.deleted ? ' deleted' : ''}`}>
                {m.reply_to && (
                  <div className="msg-quote">
                    <span>{m.reply_to.sender_id === me?.id ? 'You' : open.other.name.split(' ')[0]}</span> {m.reply_to.snippet}
                  </div>
                )}
                {m.shared_post && (m.shared_post.missing
                  ? <div className="shared-post gone">Shared a post that's no longer available</div>
                  : (
                    <button type="button" className="shared-post" onClick={() => onOpenPost?.(m.shared_post.id)}>
                      <span className="shared-post-by">Shared a post by {m.shared_post.author}</span>
                      {m.shared_post.title && <b>{m.shared_post.title}</b>}
                      {m.shared_post.snippet && <span>{m.shared_post.snippet}</span>}
                      <span className="shared-post-open">Open post →</span>
                    </button>
                  ))}
                {editingId === m.id ? (
                  <div className="fc-c-edit">
                    <textarea className="ta" value={editDraft} autoFocus onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }} />
                    <div className="fc-c-edit-actions">
                      <button className="btn quiet sm" onClick={() => setEditingId(null)}>Cancel</button>
                      <button className="btn primary sm" disabled={!editDraft.trim() && !m.shared_post && !m.attachment} onClick={() => api.editMessage(m.id, editDraft.trim())
                        .then((u) => { swap(u); setEditingId(null); }).catch((e) => notify(e.message))}>Save</button>
                    </div>
                  </div>
                ) : (<>{m.body && <RichText text={m.body} />}{m.body && !m.deleted && <ChatLinkPreview text={m.body} />}</>)}
                <ChatAttachment file={m.attachment} mine={m.sender_id === me?.id} notify={notify}
                  loadImage={() => api.messageImageUrl(m.id)}
                  download={() => api.messageFileDownload(m.id, m.attachment?.filename)}
                  save={() => api.messageFileSave(m.id)} />
                {!m.deleted && editingId !== m.id && (
                  <div className="msg-tools">
                    {m.edited && <span className="muted-sm">edited</span>}
                    {open.active && <button className="linklike" onClick={() => setReplyTo(m)}>Reply</button>}
                    {m.sender_id === me?.id && open.active && (
                      <button className="linklike" onClick={() => { setEditingId(m.id); setEditDraft(m.body); }}>Edit</button>
                    )}
                    {m.sender_id === me?.id && (
                      <button className="linklike" onClick={() => window.confirm('Delete this message? They’ll see “message deleted”.')
                        && api.deleteMessage(m.id).then(() => api.thread(open.id).then(setOpen)).catch((e) => notify(e.message))}>Delete</button>
                    )}
                  </div>
                )}
                {!m.deleted && (
                  <EmojiReactions reactions={m.reactions} myId={me?.id} label="this message"
                    onReact={(e) => api.reactToMessage(m.id, e).then(swap).catch((err) => notify(err.message))} />
                )}
              </div>
            ))}
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="people">
      <header className="conns-head people-head">
        <div>
          <h1>People</h1>
          <p className="muted-sm">Your network on B/N. Connections always need both people to say yes.</p>
        </div>
        <button className="btn apply" onClick={() => setInviteOpen(true)}>Invite someone</button>
      </header>

      <label className="people-search">
        <span aria-hidden="true">⌕</span>
        <input value={q} placeholder="Search members by name, role, skills, interests or company…" aria-label="Search members"
          onChange={(e) => { setQ(e.target.value); if (e.target.value.trim()) setTab('discover'); }} />
        {q && <button className="linklike" aria-label="Clear search" onClick={() => setQ('')}>×</button>}
      </label>

      <MentionsList notify={notify} onOpenPost={onOpenPost} />

      <div className="people-tabs" role="tablist">
        {[['connections', `My connections (${active.length})`], ['discover', 'Discover'], ['requests', 'Requests']].map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={`people-tab${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>
            {l}{k === 'requests' && proposals.length + referralAsks > 0 && <em className="people-badge">{proposals.length + referralAsks}</em>}
          </button>
        ))}
      </div>

      {tab === 'connections' && (
        <section>
          {active.length === 0 && (
            <div className="card pad empty-state">
              <b>No connections yet</b>
              <p className="muted-sm">Answer the introductions waiting under <b>Requests</b>, or find people in <b>Discover</b> and ask to connect.</p>
            </div>
          )}
          {active.map((c) => (
            <div className="person-row" key={c.id}>
              <button type="button" className="person-link" aria-label={`${c.other.name}'s profile`} onClick={() => openProfile(c.other.handle)}>
                <Avatar person={c.other} className="prop-av sm" />
              </button>
              <div className="person-main static">
                <button type="button" className="person-link name" onClick={() => openProfile(c.other.handle)}><b>{c.other.name}</b></button>
                <span className="muted-sm">{c.other.headline}</span>
                <span className="person-last">
                  {lastMessage(c.thread_id) || 'No messages yet: say hello'}
                  {unreadIn(c.thread_id) > 0 && <b className="unread-inline"> · {unreadIn(c.thread_id)} new</b>}
                </span>
              </div>
              <button className={`btn sm ${unreadIn(c.thread_id) ? 'primary' : 'ghost'}`} disabled={!c.thread_id}
                onClick={() => c.thread_id && openThread(c.thread_id)}>Message</button>
              <div className="fc-more">
                <button className="fc-more-btn" aria-label={`More for ${c.other.name}: view profile, remove connection, block`}
                  aria-haspopup="menu" aria-expanded={menuFor === c.id} onClick={() => setMenuFor(menuFor === c.id ? null : c.id)}>•••</button>
                {menuFor === c.id && (
                  <div className="fc-more-menu down" role="menu" onMouseLeave={() => setMenuFor(null)}>
                    <button role="menuitem" onClick={() => { setMenuFor(null); openProfile(c.other.handle); }}>View profile</button>
                    <button role="menuitem" onClick={() => { setMenuFor(null); sever(c); }}>Remove connection</button>
                    <button role="menuitem" className="danger" onClick={async () => {
                      setMenuFor(null);
                      if (!window.confirm(`Block ${c.other.name}? They won't be told. You won't see each other's posts or messages.`)) return;
                      try { await api.block(c.other.id); notify('Blocked.'); load(); } catch (e) { notify(e.message); }
                    }}>Block</button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {ended.length > 0 && (
            <>
              <h2 className="sec-title">Ended ({ended.length})</h2>
              {ended.map((c) => (
                <div className="person-row muted" key={c.id}>
                  <Avatar person={c.other} className="prop-av sm" />
                  <button className="person-main" onClick={() => c.thread_id && openThread(c.thread_id)} disabled={!c.thread_id}>
                    <b>{c.other.name}</b><span className="muted-sm">Conversation stays readable</span>
                  </button>
                </div>
              ))}
            </>
          )}
        </section>
      )}

      {tab === 'discover' && (
        <section>
          <p className="muted-sm">{q.trim() ? `${(results || []).length} member${(results || []).length === 1 ? '' : 's'} match “${q.trim()}”.` : 'Suggested first: people who work on what you do.'}</p>
          {results === null && <p className="muted-sm">Searching…</p>}
          <div className="discover-grid">
            {(results || []).map((r) => (
              <div className={`card pad discover-card${r.suggested ? ' suggested' : ''}`} key={r.person.id}>
                <div className="discover-top">
                  <button type="button" className="person-link" aria-label={`${r.person.name}'s profile`} onClick={() => openProfile(r.person.handle)}>
                    <Avatar person={r.person} className="prop-av sm" />
                  </button>
                  <div className="discover-who">
                    <button type="button" className="person-link name" onClick={() => openProfile(r.person.handle)}><b>{r.person.name}</b></button>
                    <span className="muted-sm">{r.person.headline}</span>
                  </div>
                  <span className={`status-chip s-${r.status}`}>{{ connected: 'Connected', pending_out: 'Pending', pending_in: 'Wants to connect', none: 'Not connected' }[r.status]}</span>
                </div>
                {r.why && <span className="discover-why">{r.why}</span>}
                <div>
                  {r.status === 'connected' && <button className="btn ghost sm" onClick={() => r.thread_id && openThread(r.thread_id)}>Message</button>}
                  {r.status === 'pending_out' && r.proposal_id && <button className="btn quiet sm" onClick={async () => {
                    try { await api.cancelConnectRequest(r.proposal_id); notify('Request cancelled. They won’t be told.'); refreshDiscover(); load(); } catch (e) { notify(e.message); }
                  }}>Cancel request</button>}
                  {r.status === 'pending_in' && <button className="btn primary sm" onClick={() => setTab('requests')}>Respond</button>}
                  {r.status === 'none' && <button className="btn apply sm" onClick={() => { setConnectTo(r.person); setNote(''); }}>Connect</button>}
                </div>
              </div>
            ))}
          </div>
          {results && results.length === 0 && <p className="muted-sm">Nobody matches that yet. Try a skill, a company or a topic, or invite them.</p>}
        </section>
      )}

      {tab === 'requests' && (
        <section>
          <div className="requests-head">
            <h2 className="sec-title">Waiting on you ({proposals.length + referralAsks})</h2>
            <button className="btn ghost sm" onClick={look} disabled={busy}>{busy ? 'Looking…' : 'Look for introductions'}</button>
          </div>
          {proposals.length + referralAsks === 0 && <p className="muted-sm">Nothing waiting. Introductions from B/N, requests from members and referrals to confirm show up here.</p>}
          {proposals.map((p) => <ProposalCard key={p.id} proposal={p} notify={notify} onDecided={() => { load(); refreshDiscover(); }} />)}
          <MyReferrals notify={notify} onChanged={load} />
          {sent.length > 0 && (
            <>
              <h2 className="sec-title">Sent ({sent.length})</h2>
              {sent.map((x) => (
                <div className="person-row" key={x.proposal_id}>
                  <Avatar person={x.person} className="prop-av sm" />
                  <div className="person-main static">
                    <button type="button" className="person-link name" onClick={() => openProfile(x.person.handle)}><b>{x.person.name}</b></button>
                    <span className="muted-sm">Pending · “{x.note}”</span>
                  </div>
                  <button className="btn quiet sm" onClick={async () => {
                    try { await api.cancelConnectRequest(x.proposal_id); notify('Request cancelled. They won’t be told.'); load(); } catch (e) { notify(e.message); }
                  }}>Cancel</button>
                </div>
              ))}
            </>
          )}
        </section>
      )}

      {connectTo && (
        <div className="modal-back" role="presentation" onClick={() => setConnectTo(null)}>
          <div className="modal card pad" role="dialog" aria-modal="true" aria-labelledby="cx-title" onClick={(e) => e.stopPropagation()}>
            <h2 id="cx-title">Connect with {connectTo.name}</h2>
            <p className="muted-sm">Say why. {connectTo.name.split(' ')[0]} sees your note and your profile, and you're connected only if they accept. They won't be told if you cancel.</p>
            <label className="flabel" htmlFor="cx-note">Your note</label>
            <textarea id="cx-note" className="ta" rows={4} maxLength={500} value={note} autoFocus onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Saw your post on PH rules — I'd love to compare notes on licensing." />
            <p className="muted-sm">You can send 3 connection requests a week.</p>
            <div className="modal-actions">
              <button className="btn quiet sm" onClick={() => setConnectTo(null)}>Cancel</button>
              <button className="btn apply sm" disabled={note.trim().length < 10 || sending} onClick={async () => {
                setSending(true);
                try {
                  const r = await api.askToConnect(connectTo.id, note.trim());
                  notify(r.status === 'connected' ? `Connected with ${connectTo.name}` : `Request sent to ${connectTo.name.split(' ')[0]}`);
                  setConnectTo(null); refreshDiscover(); load();
                } catch (e) { notify(e.message); } finally { setSending(false); }
              }}>{sending ? 'Sending…' : 'Send request'}</button>
            </div>
          </div>
        </div>
      )}

      {inviteOpen && (
        <div className="modal-back" role="presentation" onClick={() => setInviteOpen(false)}>
          <div className="modal wide card pad" role="dialog" aria-modal="true" aria-label="Invite someone" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top">
              <h2>Invite someone to B/N</h2>
              <button className="linklike" aria-label="Close" onClick={() => setInviteOpen(false)}>×</button>
            </div>
            <p className="muted-sm">Invite builders who aren't here yet. They join through your invite and you're connected automatically.</p>
            <Invites notify={notify} />
          </div>
        </div>
      )}
    </div>
  );
}
